import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/api';

interface OtherTable { bookingId: string; name: string; tableLabel: string }
interface Drink { id: string; itemName: string; itemPrice?: number; itemImage?: string }

interface Props {
  branchId: string;
  bookingId: string;
  /** Preselect a table (e.g. "send one back" to the person who just accepted). */
  presetRecipientBookingId?: string;
  presetRecipientLabel?: string;
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Send a toast or a drink to ANOTHER table in the venue. Toast-first (a free "Cheers 🍻"); a real drink
 * is an optional upgrade that lands on YOUR tab only if they accept. The recipient never sees a price.
 */
const SendDrinkModal: React.FC<Props> = ({ branchId, bookingId, presetRecipientBookingId, presetRecipientLabel, onClose, onSent }) => {
  const [tables, setTables] = useState<OtherTable[]>([]);
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [recipient, setRecipient] = useState<string | null>(presetRecipientBookingId || null);
  const [kind, setKind] = useState<'toast' | 'drink'>('toast');
  const [drinkId, setDrinkId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [t, details] = await Promise.all([
          apiService.getGiftTables(branchId, bookingId),
          apiService.getBranchDetails(branchId).catch(() => null),
        ]);
        if (!alive) return;
        setTables(t);
        const cats = ((details as any)?.data?.stockItemsByCategory || []) as any[];
        const flat: Drink[] = [];
        cats.forEach((c) => {
          (c.itemsWithoutSubCategory || []).forEach((it: any) => flat.push({ id: it.id, itemName: it.itemName, itemPrice: it.itemPrice, itemImage: it.itemImage }));
          (c.subCategories || []).forEach((sc: any) => (sc.items || []).forEach((it: any) => flat.push({ id: it.id, itemName: it.itemName, itemPrice: it.itemPrice, itemImage: it.itemImage })));
        });
        setDrinks(flat);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : 'Could not load the room');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [branchId, bookingId]);

  const recipientLabel = useMemo(() => {
    if (presetRecipientBookingId && presetRecipientLabel) return presetRecipientLabel;
    const t = tables.find((x) => x.bookingId === recipient);
    return t ? `${t.name} · ${t.tableLabel}` : '';
  }, [tables, recipient, presetRecipientBookingId, presetRecipientLabel]);

  const canSend = !!recipient && (kind === 'toast' || (kind === 'drink' && !!drinkId)) && !busy;

  const send = async () => {
    if (!canSend || !recipient) return;
    setBusy(true); setErr('');
    try {
      await apiService.sendGift({
        senderBookingId: bookingId,
        recipientBookingId: recipient,
        kind,
        stockItemId: kind === 'drink' ? drinkId : null,
        message: message.trim() || null,
        anonymous,
      });
      setDone(true);
      onSent?.();
      setTimeout(onClose, 1400);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <span style={s.title}>🍻 Send to another table</span>
          <button style={s.x} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {done ? (
          <div style={s.doneBox}>
            <div style={{ fontSize: 40 }}>🍻</div>
            <div style={s.doneTitle}>Sent!</div>
            <div style={s.hint}>We'll let you know when they raise a glass back.</div>
          </div>
        ) : loading ? (
          <div style={s.hint}>Loading the room…</div>
        ) : (
          <>
            {/* 1. Pick a table (unless preset) */}
            {!presetRecipientBookingId && (
              <div style={s.section}>
                <div style={s.label}>Who's it for?</div>
                {tables.length === 0 ? (
                  <div style={s.hint}>No other tables are seated right now.</div>
                ) : (
                  <div style={s.tableList}>
                    {tables.map((t) => (
                      <button
                        key={t.bookingId}
                        style={{ ...s.tableChip, ...(recipient === t.bookingId ? s.tableChipOn : {}) }}
                        onClick={() => setRecipient(t.bookingId)}
                      >
                        <strong>{t.tableLabel}</strong>
                        <span style={s.tableName}>{t.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {presetRecipientBookingId && recipientLabel && (
              <div style={s.section}><div style={s.label}>To</div><div style={s.presetTo}>{recipientLabel}</div></div>
            )}

            {/* 2. Toast or drink */}
            <div style={s.section}>
              <div style={s.label}>What kind of gesture?</div>
              <div style={s.segment}>
                <button style={{ ...s.segBtn, ...(kind === 'toast' ? s.segOn : {}) }} onClick={() => setKind('toast')}>
                  🍻 Toast<span style={s.segSub}>Free · a cheers</span>
                </button>
                <button style={{ ...s.segBtn, ...(kind === 'drink' ? s.segOn : {}) }} onClick={() => setKind('drink')}>
                  🍸 Send a drink<span style={s.segSub}>On your tab if accepted</span>
                </button>
              </div>
            </div>

            {/* 3. Drink picker */}
            {kind === 'drink' && (
              <div style={s.section}>
                <div style={s.label}>Pick a drink</div>
                {drinks.length === 0 ? (
                  <div style={s.hint}>No menu items available.</div>
                ) : (
                  <div style={s.drinkList}>
                    {drinks.map((d) => (
                      <button
                        key={d.id}
                        style={{ ...s.drinkRow, ...(drinkId === d.id ? s.drinkOn : {}) }}
                        onClick={() => setDrinkId(d.id)}
                      >
                        <span>{d.itemName}</span>
                        {drinkId === d.id && <span>✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 4. Message + anonymity */}
            <div style={s.section}>
              <input
                style={s.input}
                placeholder="Add a short message (optional)"
                maxLength={200}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <label style={s.anon}>
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
                <span>Send as a secret admirer 🤫</span>
              </label>
            </div>

            {err && <div style={s.err}>{err}</div>}

            <button style={{ ...s.send, ...(canSend ? {} : s.sendOff) }} disabled={!canSend} onClick={send}>
              {busy ? 'Sending…' : kind === 'toast' ? 'Send a toast 🍻' : 'Send the drink 🍸'}
            </button>
            <div style={s.footHint}>The other table only sees your gesture — never a price.</div>
          </>
        )}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  sheet: { width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', background: 'var(--surface, #fff)', color: 'var(--text, #111)', borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: 18, boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 18, fontWeight: 800 },
  x: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-3, #888)' },
  section: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--text-2, #444)' },
  hint: { fontSize: 13, color: 'var(--text-3, #888)', padding: '6px 0' },
  tableList: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tableChip: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '10px 14px', borderRadius: 12, border: '1px solid var(--line, #e5e7eb)', background: 'var(--surface-2, #f7f7f8)', cursor: 'pointer', minWidth: 96 },
  tableChipOn: { border: '2px solid #d97706', background: 'rgba(217,119,6,0.08)' },
  tableName: { fontSize: 12, color: 'var(--text-3, #888)' },
  presetTo: { fontWeight: 700, fontSize: 15 },
  segment: { display: 'flex', gap: 8 },
  segBtn: { flex: 1, display: 'flex', flexDirection: 'column', gap: 3, padding: '12px 10px', borderRadius: 12, border: '1px solid var(--line, #e5e7eb)', background: 'var(--surface-2, #f7f7f8)', cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  segOn: { border: '2px solid #d97706', background: 'rgba(217,119,6,0.08)' },
  segSub: { fontSize: 11, fontWeight: 500, color: 'var(--text-3, #888)' },
  drinkList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' },
  drinkRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--line, #e5e7eb)', background: 'var(--surface-2, #f7f7f8)', cursor: 'pointer', fontSize: 14, textAlign: 'left' },
  drinkOn: { border: '2px solid #d97706', background: 'rgba(217,119,6,0.08)', fontWeight: 700 },
  input: { padding: '11px 12px', borderRadius: 10, border: '1px solid var(--line, #e5e7eb)', background: 'var(--surface-2, #f7f7f8)', color: 'inherit', fontSize: 14 },
  anon: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-2, #444)' },
  err: { marginTop: 12, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 13 },
  send: { marginTop: 16, width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: '#d97706', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer' },
  sendOff: { opacity: 0.5, cursor: 'not-allowed' },
  footHint: { marginTop: 8, textAlign: 'center', fontSize: 12, color: 'var(--text-3, #888)' },
  doneBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '30px 0' },
  doneTitle: { fontSize: 20, fontWeight: 800 },
};

export default SendDrinkModal;
